import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  DollarSign,
} from 'lucide-react';
import { apiRequest } from '../lib/api';

export default function Payouts() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('pending');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-payouts', page, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '30',
        ...(statusFilter && { status: statusFilter }),
      });
      return apiRequest('GET', `/api/admin/payouts?${params}`);
    },
  });

  const processMutation = useMutation({
    mutationFn: ({ id, action, tx_hash }: { id: string; action: 'approve' | 'reject'; tx_hash?: string }) =>
      apiRequest('PUT', `/api/admin/payouts/${id}`, { action, tx_hash }),
    onSuccess: () => {
      toast.success('Payout processed successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-payouts'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to process payout');
    },
  });

  const handleApprove = (id: string) => {
    const txHash = prompt('Enter transaction hash (optional):');
    processMutation.mutate({ id, action: 'approve', tx_hash: txHash || undefined });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-white">Payouts</h1>
          <p className="text-sm text-zinc-500">Process referral payout requests</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-black border border-white/10 text-zinc-300"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : (
        <>
          <div className="border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">User</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Amount</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Status</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Requested</th>
                  <th className="text-right text-xs text-zinc-500 px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.payouts?.map((payout: any) => (
                  <tr key={payout.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 text-xs font-mono text-zinc-300">
                      {payout.user_id.slice(0, 6)}...{payout.user_id.slice(-4)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3 text-emerald-500" />
                        <span className="text-sm font-mono text-white">
                          {parseFloat(payout.amount).toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 ${
                        payout.status === 'completed'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : payout.status === 'pending'
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {new Date(payout.requested_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {payout.status === 'pending' && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleApprove(payout.id)}
                            disabled={processMutation.isPending}
                            className="p-1.5 text-emerald-400 hover:bg-emerald-500/20"
                            title="Approve"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => processMutation.mutate({ id: payout.id, action: 'reject' })}
                            disabled={processMutation.isPending}
                            className="p-1.5 text-red-400 hover:bg-red-500/20"
                            title="Reject"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data?.pagination && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-zinc-500">
                Showing {data.payouts.length} of {data.pagination.total} payouts
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-zinc-400">
                  Page {page} of {data.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.totalPages}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
